import React, { useState } from 'react';
import { Alert, Grid, IconButton, Typography } from '@mui/material';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { CountryPhoneNumber } from '../../../../PersonalInfoTab/Form/EmergencyContact/CountryPhoneNumber';
import { useTranslation } from 'react-i18next';
import { FormInputDropdown } from '../../../../PersonalInfoTab/Form/Controls/FormInputDropdown';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { SimpleCheckbox } from '@AMIEWEB/Unit/Common/SimpleCheckbox';
import FormInputTextField from '@AMIEWEB/Candidate/CandidateProfile/WorkExperience/Edit/components/FormInputTextField';
import { useAddEditReferenceStyle } from './AddEditReference.style';

const ReferenceContacts = (props: any) => {
  const { phoneTypeOptions, contactPreferenceOptions, bestTimeToReachOptions, displayAlert, disabled } = props;
  const { classes } = useAddEditReferenceStyle();
  const { t } = useTranslation();

  const {
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = useFormContext();

  const [secondPhoneEnabled, setSecondPhoneEnabled] = useState<boolean>(watch('phoneType2') && watch('phoneNumber2'));

  const checkSaveDisabled = () => {
    if (Object.keys(errors).length > 0) {
      props.setSaveDisabled(true);
    } else {
      props.setSaveDisabled(false);
    }
  };

  const handleCountryPhone1Change = (value, data, event, formattedValue) => {
    if (formattedValue && displayAlert) {
      props.setDisplayAlert(false);
    }
    setValue('phoneNumber1', formattedValue, { shouldDirty: true });
    checkSaveDisabled();
  };

  const handleCountryPhone2Change = (value, data, event, formattedValue) => {
    if (formattedValue && displayAlert) {
      props.setDisplayAlert(false);
    }
    setValue('phoneNumber2', formattedValue, { shouldDirty: true });
    checkSaveDisabled();
  };

  const validateEmail = (value: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(value);
  };

  const handleOnEmailChange = value => {
    if (value && displayAlert) {
      props.setDisplayAlert(false);
    }

    if (value && !validateEmail(value)) {
      setError('email', {
        message: t('candidate.workExperience.addEditReference.referenceContacts.invalidEmailErrorMessage'),
      });
    } else {
      clearErrors('email');
    }

    checkSaveDisabled();
  };

  return (
    <Grid container columnSpacing={6} rowSpacing={4} px={4} pt={6} pb={2}>
      {displayAlert && (
        <Grid item xs={12}>
          <Typography>
            <Alert severity="error">
              {t('candidate.workExperience.addEditReference.referenceContacts.emailPhoneNumberError')}
            </Alert>
          </Typography>
        </Grid>
      )}
      <Grid item xs={4}>
        <FormInputTextField
          name="supervisorName"
          label={t('candidate.workExperience.addEditReference.referenceContacts.supervisorEvaluator')}
          required={true}
          disabled={disabled}
          maxLength={25}
          maxLengthErrorMessage={t('candidate.workExperience.addEditReference.referenceContacts.textFieldErrorMessage')}
        />
      </Grid>
      <Grid item xs={4} display={'flex'}>
        <Grid item xs={7}>
          <FormInputDropdown
            name="phoneType1"
            label={`${t('candidate.workExperience.addEditReference.referenceContacts.phoneType')} ${
              getValues('phoneNumber1') &&
              getValues('email') &&
              (watch('contactMethod') === 'Call' || watch('phoneNumber1'))
                ? '*'
                : ''
            }`}
            require={
              getValues('phoneNumber1') &&
              getValues('email') &&
              (watch('contactMethod') === 'Call' || watch('phoneNumber1'))
                ? true
                : false
            }
            disabled={disabled}
            options={phoneTypeOptions}
            handleChange={event => {
              if (event.target.value) clearErrors('phoneType1');
              setValue('phoneType1', event.target.value, { shouldDirty: true });
              checkSaveDisabled();
            }}
            message={errors['phoneType1']?.message || ''}
            standard
            showErrorAttribute={true}
            useMenuItem
          />
        </Grid>
        {!secondPhoneEnabled && (
          <Grid item xs={2} mt={2.5} ml={4}>
            <IconButton
              color="primary"
              type="button"
              classes={{ root: classes.addButtonRoot }}
              disabled={disabled}
              disableTouchRipple
              disableFocusRipple
              onClick={() => setSecondPhoneEnabled(true)}
              aria-label="phone-add-button"
              size="small"
            >
              <AddBoxIcon sx={{ height: 30, width: 30 }} />
            </IconButton>
          </Grid>
        )}
      </Grid>
      <Grid item xs={4}>
        <FormInputTextField
          name="email"
          label={`${t('candidate.workExperience.addEditReference.referenceContacts.email')} ${
            watch('contactMethod') === 'Email' ? '*' : ''
          }`}
          required={false}
          disabled={disabled}
          onChange={event => {
            handleOnEmailChange(event?.target?.value);
            setValue('email', event?.target?.value, { shouldDirty: true });
          }}
          onBlur={event => handleOnEmailChange(event?.target?.value)}
          maxLength={255}
          maxLengthErrorMessage={t('candidate.workExperience.addEditReference.referenceContacts.textFieldErrorMessage')}
        />
      </Grid>
      <Grid item xs={4}>
        <FormInputTextField
          name="title"
          label={t('candidate.workExperience.addEditReference.referenceContacts.title')}
          required={false}
          disabled={disabled}
          maxLength={255}
          maxLengthErrorMessage={t('candidate.workExperience.addEditReference.referenceContacts.textFieldErrorMessage')}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          control={control}
          name="phoneNumber1"
          render={({ field }) => (
            <CountryPhoneNumber
              className={classes.phonelabel}
              label={`${t('candidate.workExperience.addEditReference.referenceContacts.phoneNumber')} ${
                getValues('phoneNumber1') &&
                getValues('email') && 
                watch('contactMethod') === 'Call' ? '*' : ''
              }`}
              onChange={handleCountryPhone1Change}
              isDisabled={disabled}
              value={field.value}
              countryCodeEditable={true}
              inlineStyle={{ width: '100%', height: '54px' }}
            />
          )}
        />
      </Grid>
      <Grid item xs={3}>
        {getValues('okToContact') && (
          <FormInputDropdown
            name="bestTimeToReach"
            label={t('candidate.workExperience.addEditReference.referenceContacts.bestTimeToReach')}
            require={false}
            disabled={disabled}
            options={bestTimeToReachOptions}
            handleChange={event => {
              setValue('bestTimeToReach', event.target.value, { shouldDirty: true });
              checkSaveDisabled();
            }}
            message={errors['bestTimeToReach']?.message || ''}
            standard
            useMenuItem
          />
        )}
      </Grid>
      <Grid item xs={4}>
        <Grid item xs={6} ml={2}>
          <Controller
            name={'okToContact'}
            control={control}
            render={({ field: { ref, onChange, value, ...rest } }) => (
              <SimpleCheckbox
                isdisabled={disabled}
                isChecked={value || false}
                handleChecked={event => {
                  onChange(event.target.checked);
                }}
                checkboxLabel={t('candidate.workExperience.addEditReference.referenceContacts.okToContact')}
                checkboxName={'AddReferenceOkToContact'}
                id={'AddReferenceOkToContact'}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid item xs={4} display={'flex'}>
        {secondPhoneEnabled && (
          <>
            <Grid item xs={7}>
              <FormInputDropdown
                name="phoneType2"
                label={`${t('candidate.workExperience.addEditReference.referenceContacts.phoneType')} ${
                  watch('phoneNumber2') ? '*' : ''
                }`}
                require={watch('phoneNumber2') ? true : false}
                disabled={disabled}
                options={phoneTypeOptions}
                handleChange={event => {
                  setValue('phoneType2', event.target.value, { shouldDirty: true });
                  checkSaveDisabled();
                }}
                message={errors['phoneType2']?.message || ''}
                standard
                showErrorAttribute={true}
                useMenuItem
              />
            </Grid>
            <Grid item xs={2} mt={2.5} ml={4}>
              <IconButton
                color="primary"
                type="button"
                classes={{ root: classes.addButtonRoot }}
                disabled={disabled}
                disableTouchRipple
                disableFocusRipple
                onClick={() => setSecondPhoneEnabled(false)}
                aria-label="phone-add-button"
                size="small"
              >
                <DeleteOutline sx={{ height: 24, width: 24 }} />
              </IconButton>
            </Grid>
          </>
        )}
      </Grid>
      <Grid item xs={4}>
        {getValues('phoneNumber1') && getValues('email') && (
          <Grid item xs={8.88}>
            <FormInputDropdown
              name="contactMethod"
              label={t('candidate.workExperience.addEditReference.referenceContacts.contactPreference')}
              require={false}
              disabled={disabled}
              options={contactPreferenceOptions}
              handleChange={event => {
                clearErrors('phoneType1');
                setValue('contactMethod', event.target.value, { shouldDirty: true });
                checkSaveDisabled();
              }}
              message={errors['contactMethod']?.message || ''}
              standard
              showErrorAttribute={true}
              useMenuItem
            />
          </Grid>
        )}
      </Grid>
      {secondPhoneEnabled && (
        <Grid item xs={4} m={'auto'}>
          <Controller
            control={control}
            name="phoneNumber2"
            render={({ field: { ref, onChange, value, ...rest } }) => (
              <CountryPhoneNumber
                className={classes.phonelabel}
                label={t('candidate.workExperience.addEditReference.referenceContacts.phoneNumber')}
                onChange={handleCountryPhone2Change}
                isDisabled={disabled}
                value={value}
                countryCodeEditable={true}
                inlineStyle={{ width: '100%', height: '54px' }}
              />
            )}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default React.memo(ReferenceContacts);
